import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Users, Percent, CreditCard, User } from "lucide-react";
import { RentalWithRelations, ShiftWithRelations } from "@/types/models";
import { calculateShiftOutstandingBalance, calculateTotalPaid, formatDateFull, calculateTotalInReview, getShiftPercentages } from '@/lib/utils';
import { useQuery } from "@tanstack/react-query";
import { getCurrentExchangeRate } from "@/integrations/supabase/services";
import { getShiftPayments } from "@/integrations/supabase/shift-services";
import { useMemo } from "react";

interface ShiftDetailsModalProps {
  shift: ShiftWithRelations | null;
  isOpen: boolean;
  onClose: () => void;
  completedRentals: RentalWithRelations[];
  rentalsLoading: boolean;
}

const ShiftDetailsModal = ({ shift, isOpen, onClose, completedRentals, rentalsLoading }: ShiftDetailsModalProps) => {
  const { data: payments = [], isLoading: paymentsLoading } = useQuery({
    queryKey: ['shift-payments', shift?.id],
    queryFn: () => getShiftPayments(shift?.id || ''),
    enabled: isOpen && !!shift,
  });

  const { data: currentRate } = useQuery({
    queryKey: ['currentExchangeRate'],
    queryFn: getCurrentExchangeRate,
  });

  const getShiftStatus = (shift: ShiftWithRelations) => {
    switch (shift.status) {
      case 'open':
        return <Badge variant="secondary">Activa</Badge>;
      case 'closed':
        return <Badge variant="destructive">Cerrada</Badge>;
      case 'in_review':
        return <Badge variant="destructive">Revisión</Badge>;
      case 'paid':
        return <Badge variant="default">Pagada</Badge>;
      default:
        return <Badge>Desconocido</Badge>;
    }
  };

  // Calculate overall financial figures
  const totalPaid = calculateTotalPaid(payments);
  const inReviewAmount = calculateTotalInReview(payments);
  const outstandingBalance = shift ? calculateShiftOutstandingBalance(shift, totalPaid) : 0;
  const exchangeRate = currentRate?.usd_to_bs ?? 0;

  const operatorAmountInBs = useMemo(() => 
    shift?.operator_amount && exchangeRate ? Math.ceil(shift.operator_amount * exchangeRate - 0.00001) : 0,
    [shift?.operator_amount, exchangeRate]
  );
  const adminAmountInBs = useMemo(() =>
    shift?.amount_admin && exchangeRate ? Math.ceil(shift.amount_admin * exchangeRate - 0.00001) : 0,
    [shift?.amount_admin, exchangeRate]
  );

  const { operatorPercentage: operatorPercent, administratorPercentage: adminPercent } = useMemo(() => {
    return getShiftPercentages(shift);
  }, [shift]);

  const outstandingBalanceInBs = useMemo(() =>
    outstandingBalance && exchangeRate ? Math.ceil(outstandingBalance * exchangeRate - 0.00001) : 0,
    [outstandingBalance, exchangeRate]
  );

  const inReviewAmountInBs = useMemo(() =>
    inReviewAmount && exchangeRate ? Math.ceil(inReviewAmount * exchangeRate - 0.00001) : 0,
    [inReviewAmount, exchangeRate]
  );

  const saldoPendienteAmount = (-1) * outstandingBalance - inReviewAmount;
  const saldoPendienteInBs = useMemo(() =>
    saldoPendienteAmount && exchangeRate ? Math.ceil(saldoPendienteAmount * exchangeRate - 0.00001) : 0,
    [saldoPendienteAmount, exchangeRate]
  );

  if (!shift) return null;

  // Determine amount owed by operator to admin for per-transaction running balance calculation
  // This should be 0 if the shift is active, or admin's share if closed.
  const amountBaseForPerTransactionDisplay = (shift.end_time) ? (shift.amount_admin || 0) : 0;
  let runningTotalPaid = 0; // Initialize for per-payment calculation, this will sum payments by operator

  // Sort payments by date for correct running balance calculation
  const sortedPayments = [...payments].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalles de la Jornada</DialogTitle>
          <DialogDescription>
            Información completa de la jornada del {formatDateFull(shift.start_time)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información General */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Información General
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Operador</p>
                  <p className="font-medium">{shift.operator?.full_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <div className="mt-1">
                    {getShiftStatus(shift)}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Inicio</p>
                  <p className="font-medium">{formatDateFull(shift.start_time)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fin</p>
                  <p className="font-medium">
                    {shift.end_time ? formatDateFull(shift.end_time) : 'En curso'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resumen Financiero */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Resumen Financiero
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarSign className="h-5 w-5 text-green-600 mr-2" />
                  <span>Total Recaudado</span>
                </div>
                <span className="font-bold text-green-600">${(shift.total_collected || 0).toFixed(2)}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-2" />
                  <span>Alquileres</span>
                </div>
                <span className="font-bold">{shift.total_rentals}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-blue-600 mr-2" />
                  <span>Alquileres Completados</span>
                </div>
                <span className="font-bold">{completedRentals.length}</span>
              </div>
              
              {shift.end_time && (
                <>
                  <hr />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Percent className="h-5 w-5 text-green-600 mr-2" />
                      <span>Parte del Operador ({operatorPercent}%)</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-green-600">${shift.operator_amount.toFixed(2)}</span>
                      {exchangeRate > 0 && (
                        <p className="text-xs text-muted-foreground">{operatorAmountInBs.toLocaleString('es-VE')} Bs</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Percent className="h-5 w-5 text-blue-600 mr-2" />
                      <span>Parte del Administrador ({adminPercent}%)</span>
                    </div>
                    <div className="text-right">
                      <span className="font-bold text-blue-600">${(shift.amount_admin || 0).toFixed(2)}</span>
                      {exchangeRate > 0 && (
                        <p className="text-xs text-muted-foreground">{adminAmountInBs.toLocaleString('es-VE')} Bs</p>
                      )}
                    </div>
                  </div>
                  <hr />
                </>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  Resumen Financiero (Operador)
                </div>
              </div>                
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarSign className="h-5 w-5" />
                  <span>Deuda Total</span>
                </div>
                <div className="text-right">
                  <span className="font-bold">${(outstandingBalance).toFixed(2)}</span>
                  {exchangeRate > 0 && (
                    <p className="text-xs text-muted-foreground">{outstandingBalanceInBs.toLocaleString('es-VE')} Bs</p>
                  )}
                </div>
              </div>
              {inReviewAmount > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <DollarSign className="h-5 w-5 text-amber-600" />
                    <span className="text-amber-600">Pagos en Revisión</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-amber-600">${inReviewAmount.toFixed(2)}</span>
                    {exchangeRate > 0 && (
                      <p className="text-xs text-muted-foreground">{inReviewAmountInBs.toLocaleString('es-VE')} Bs</p>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <DollarSign className="h-5 w-5 text-orange-600" />
                  <span className="text-orange-600 font-bold">Saldo Pendiente</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-orange-600">${(saldoPendienteAmount).toFixed(2)}</span>
                  {exchangeRate > 0 && (
                    <p className="text-xs text-muted-foreground">{saldoPendienteInBs.toLocaleString('es-VE')} Bs</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pagos */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Pagos Registrados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {paymentsLoading ? (
                <p className="text-center py-4">Cargando pagos...</p>
              ) : payments.length === 0 ? (
                <p className="text-center py-4 text-muted-foreground">No hay pagos registrados</p>
              ) : (
                <div className="space-y-4">
                  {sortedPayments.map((payment) => {
                    // runningTotalPaid accumulates the sum of payments made by the operator
                    runningTotalPaid += (payment.status === 'approved' ? payment.amount : 0);
                    // balanceAfterThisPayment shows what operator still owes to admin after this payment
                    const balanceAfterThisPayment = amountBaseForPerTransactionDisplay - runningTotalPaid;
                    const amountInBs = (payment.amount || 0) * (payment.exchange_rate || 0);
                    return (
                      <div key={payment.id} className="border rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                          <div>
                            <p className="text-sm text-muted-foreground">Banco</p>
                            <p className="font-medium">{payment.bank?.name || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Monto Pagado</p>
                            <p className="font-medium text-green-600">${(payment.amount || 0).toFixed(2)} USD</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Referencia</p>
                            <p className="font-medium">{payment.reference || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Tasa de Cambio</p>
                            <p className="font-medium">{(payment.exchange_rate || 0).toFixed(2)} Bs/USD</p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-muted-foreground">Fecha del Pago</p>
                            <p className="font-medium">{formatDateFull(payment.created_at)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Monto Pagado (Bs)</p>
                            <p className="font-medium">{amountInBs.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Bs</p>
                          </div>
                        </div>
                        {payment.receipt_photo_url && (
                          <div> {/* Changed from col-span-2 to a separate div for layout flow */}
                            <p className="text-sm text-muted-foreground mb-1">Comprobante</p>
                            <img 
                              src={payment.receipt_photo_url} 
                              alt="Comprobante de pago"
                              className="max-w-xs rounded border"
                            />
                          </div>
                        )}
                        <div className="pt-2 border-t">
                          {payment.status === 'approved' ? (
                            <div>
                            <p className="text-xs text-muted-foreground"><b>Saldo Pendiente</b> después de este Pago</p>
                            <p className={`font-semibold ${balanceAfterThisPayment > 0 ? 'text-orange-500' : 'text-green-700'}`}>
                            ${balanceAfterThisPayment.toFixed(2)}
                            </p>
                            </div>
                          ) : payment.status === 'rejected' ? (
                            <div>
                              <p className="font-semibold text-red-600">
                                Pago Rechazado.
                              </p>
                              <p className="text-xs text-muted-foreground mt-1"><b>Motivo:</b> {payment.rejection_reason}</p>
                            </div>
                          ) : (
                          <div>
                            <p className="font-semibold text-gray-500">
                              {payment.status === 'pending' && 'Pendiente de Aprobación'}
                            </p>
                          </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftDetailsModal;
